import React, { ReactNode, useContext, useEffect, useState } from "react";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import { AxiosResponse } from "axios";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import { ProductContext } from "../../../contexts/ProductContext";
import TestRequestApi from "../../../api/services/TestRequestApi";
import CustomRadioGroup from "../../../components/Input/CustomRadioGroup";
import CustomTextField from "../../../components/Input/CustomTextField";
import CustomDatePicker from "../../../components/Input/CustomDatePicker";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { SampleType, WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import { parseDate } from "../../../api/services/common/FormatDateService";
import InfoBox from "../../../components/InfoBox";
import GalleryUploadButton from "../../../components/Gallery/button/GalleryUploadButton";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import HiddenError from "../../../models/HiddenError";
import ProductSampleGallery from "./ProductSampleGallery";
import ProductAttachment from "../../../api/models/product/ProductAttachment";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ProductSide, { PRODUCT_SIDES, PRODUCT_SIDE_DICTIONARY } from "../models/ProductSide.model";
import { ProductSample, TesaPrototypeSample } from "../../../api/models/product/ProductSampleReference";
import ProductSamplesApi from "../../../api/services/ProductSamplesApi";
import ProductType from "../../../api/models/product/ProductType";
import { TesaPrototype } from "../../../api/models/product/TesaPrototype";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";

type Deliverer = TestRequestRaw.Schemas.Deliverer;

interface ProductSampleDetailsPrototypeForm {
  id?: number;
  sampleName: string;
  prototypeName: string;
  productSampleID: number;
  receivedOn: Date | null;
  source: string;
  responsibleDeveloper: string;
  responsibleLaboratoryName: string;
  responsibleLaboratoryId: string | number;
  sideOfProduct: ProductSide;
  deliveredBy: Deliverer;
  manufacturingDate: Date | null;
  descriptionOrNotes: string;
  attachments?: TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[];
}

interface ProductSampleDetailsPrototypeDialogProps extends CustomDialogProps {
  apiReqType: string;
  disabled?: boolean;
  testRequestID: string;
  prototypeProperties?: TesaPrototype | null;
  callback: (closeDialog: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: theme.spacing(5),
    },
  }),
);

const ProductSampleDetailsPrototypeDialog = (props: ProductSampleDetailsPrototypeDialogProps) => {
  const { prototypeProperties, callback, disabled, open, onClose, testRequestID } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const productSamplesApi = new ProductSamplesApi(api);
  const { prototypeObject, laboratoryObject, productObject, updateProductObject } = useContext(ProductContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const classes = useStyles(props);
  const [sampleName, setSampleName] = useState("");
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<ProductSampleDetailsPrototypeForm | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  let formRef: FormikProps<ProductSampleDetailsPrototypeForm> | null = null;

  const { productSamples, newProductSample } = React.useContext(TestRequestPermissionContext);
  const isPredefined = !!prototypeObject?.usedIn;
  const permissions = productObject ? productSamples?.[productObject?.id] : newProductSample;
  const formFieldsDisabled = disabled || !permissions?.editDefinition;

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  const yupValidationSchema = Yup.object().shape({
    sampleName: Yup.string()
      .required("Please enter a sample name")
      .test(
        "invalid character test",
        "Name cannot contain a hash '#' symbol",
        (value) => value && !value.includes("#"),
      ),
    prototypeName: Yup.string(),
    productSampleID: Yup.number(),
    receivedOn: Yup.string().nullable(),
    source: Yup.string(),
    responsibleDeveloper: Yup.string(),
    sideOfProduct: Yup.string().required(),
    deliveredBy: Yup.string().required(),
    manufacturingDate: Yup.string().nullable(),
    descriptionOrNotes: Yup.string(),
  });

  useEffect(() => {
    setSampleName(productObject?.productSampleName || prototypeObject?.name || "");
  }, [prototypeObject, productObject]);

  const showGallery = open && (productObject || productObject?.id === 0) && testRequestID && productObject?.attachments;
  const showExtraSaveButton = !disabled && !showGallery && !productObject?.id && permissions?.editAttachments;
  const showGalleryAddButton = !disabled && showGallery && permissions?.editAttachments;

  const save = (values: ProductSampleDetailsPrototypeForm): Promise<ProductSample> => {
    const payload: TesaPrototypeSample = {
      attachments: values.attachments as TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[],
      id: productObject?.id as number,
      deliveredBy: values.deliveredBy,
      descriptionOrNotes: values.descriptionOrNotes,
      manufacturingDate: values.manufacturingDate?.toISOString() as string,
      productSampleName: values.sampleName,
      prototypeDeveloper: values.responsibleDeveloper,
      prototypeId: values.productSampleID || prototypeObject.id,
      prototypeLaboratoryId: +values.responsibleLaboratoryId,
      prototypeLaboratoryName: values.responsibleLaboratoryName,
      prototypeName: values.prototypeName,
      receivedOn: values.receivedOn?.toISOString() as string,
      sideOfProduct: values.sideOfProduct || "",
      source: values.source,
      type: "TESA_PROTOTYPE" as ProductType,
    };

    if (productObject) {
      return productSamplesApi.updateProductSample(testRequestID, payload as TesaPrototypeSample).then((res) => {
        showSnackbar("Product sample updated", "success");
        return res.data;
      });
    }

    return testRequestApi.createPrototypeSample(payload).then((res: AxiosResponse) => {
      if (permissions?.editDefinition && testRequestID) {
        const newProductSampleId = Number(res.data.id);
        return productSamplesApi.updateTestRequestProducts(testRequestID, [newProductSampleId]).then(() => {
          showSnackbar("Product sample created", "success");
          onClose();
          return {
            ...res.data,
            attachments: [],
          };
        });
      }
      return res.data;
    });
  };

  const update = (
    values: ProductSampleDetailsPrototypeForm,
    actions: FormikHelpers<ProductSampleDetailsPrototypeForm>,
  ): void => {
    if (disabled || !formRef?.dirty || !permissions?.editDefinition) {
      actions.resetForm();
      callback(true);
      onClose();
    } else {
      const fieldsToCheck = [values.sampleName, values.source, values.descriptionOrNotes, values.responsibleDeveloper];

      if (confidentialRegex && containsHighlyConfidentialInfo(fieldsToCheck, confidentialRegex)) {
        setPendingValues(values);
        setShowConfidentialDialog(true);
        return;
      }

      save(values)
        .then(() => {
          actions.resetForm();
          callback(true);
          onClose();
        })
        .catch(handleHttpError);
    }
  };

  return (
    <>
      <ProductContext.Consumer>
        {() => {
          return (
            <CustomDialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
              <h2 className={classes.header}>
                {isPredefined ? (
                  <WithPredefinedChip
                    details={{
                      testRequestId: prototypeObject.usedIn,
                      sampleType: SampleType.Product,
                    }}
                    spacing={2}
                    text={`tesa PRODUCT ${sampleName}`}
                  />
                ) : (
                  <>tesa PROTOTYPE {sampleName}</>
                )}{" "}
              </h2>
              <h3 className={classes.header}>Product Sample Definition</h3>
              <Formik
                innerRef={(ref) => {
                  if (ref) {
                    formRef = ref;
                  }
                }}
                initialValues={{
                  sampleName: productObject?.productSampleName || prototypeObject?.name || prototypeProperties?.name,
                  prototypeName: productObject?.prototypeName || prototypeObject?.name || prototypeProperties?.name,
                  productSampleID: productObject?.id || prototypeProperties?.id,
                  receivedOn: parseDate(productObject?.receivedOn),
                  source: productObject?.source || "",
                  responsibleDeveloper:
                    productObject?.prototypeDeveloper ||
                    prototypeObject?.responsibleDeveloper ||
                    prototypeProperties?.responsibleDeveloper ||
                    "",
                  responsibleLaboratoryId:
                    prototypeProperties?.responsibleLaboratory.id ||
                    productObject?.prototypeLaboratoryId ||
                    laboratoryObject?.id,
                  responsibleLaboratoryName:
                    prototypeProperties?.responsibleLaboratory.name ||
                    productObject?.prototypeLaboratoryName ||
                    laboratoryObject?.name,
                  sideOfProduct: productObject?.sideOfProduct || "OPEN",
                  deliveredBy: productObject?.deliveredBy || "",
                  manufacturingDate: parseDate(productObject?.manufacturingDate),
                  descriptionOrNotes: productObject?.descriptionOrNotes || "",
                }}
                onSubmit={update}
                validationSchema={yupValidationSchema}
              >
                {(formikProps: FormikProps<ProductSampleDetailsPrototypeForm>): ReactNode => {
                  const { errors, isSubmitting, handleBlur, handleChange, setFieldValue, touched, values } =
                    formikProps;
                  return (
                    <Form>
                      <Box pb={4}>
                        <Grid container justify="flex-start" direction="row" spacing={4}>
                          <Grid item lg={6} md={6} sm={6} xs={6}>
                            <InfoBox text="Keep the sample name as short as possible.">
                              <CustomTextField
                                autoFocus
                                fullWidth
                                disabled={formFieldsDisabled}
                                mandatory
                                name="sampleName"
                                id="sampleName"
                                label="Sample Name"
                                value={values.sampleName}
                                type="sampleName"
                                helperText={[errors.sampleName && touched.sampleName ? errors.sampleName : ""].join(
                                  " ",
                                )}
                                error={!!(errors.sampleName && touched.sampleName)}
                                onChange={(e) => {
                                  setSampleName(e.target.value);
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="prototypeName"
                              id="prototypeName"
                              label="Prototype Name"
                              value={values.prototypeName}
                              type="prototypeName"
                              helperText={[
                                errors.prototypeName && touched.prototypeName ? errors.prototypeName : "",
                              ].join(" ")}
                              error={!!(errors.prototypeName && touched.prototypeName)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            {productObject && (
                              <CustomTextField
                                fullWidth
                                disabled
                                name="productSampleID"
                                id="productSampleID"
                                label="Product Sample ID"
                                value={values.productSampleID}
                                type="productSampleID"
                                helperText={[
                                  errors.productSampleID && touched.productSampleID ? errors.productSampleID : "",
                                ].join(" ")}
                                error={!!(errors.productSampleID && touched.productSampleID)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            )}
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              label="Received On"
                              value={values.receivedOn}
                              onChange={(date) => setFieldValue("receivedOn", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDropdown
                              disabled={formFieldsDisabled}
                              helperText={[errors.source && touched.source ? errors.source : ""].join(" ")}
                              labelText="Source"
                              name="source"
                              value={values.source}
                              onChange={(e): void => {
                                setFieldValue("source", e.target.value);
                              }}
                              error={!!(errors.source && touched.source)}
                            >
                              <MenuItem value="">
                                <em>Please Choose</em>
                              </MenuItem>
                              <MenuItem value="LABORATORY">Laboratory</MenuItem>
                              <MenuItem value="TECHNICAL_TRIAL">Technical Trial</MenuItem>
                              <MenuItem value="PRODUCTION_TRIAL">Production Trial</MenuItem>
                            </CustomDropdown>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="responsibleDeveloper"
                              id="responsibleDeveloper"
                              label="Responsible Developer"
                              value={values.responsibleDeveloper}
                              type="responsibleDeveloper"
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="responsibleLaboratoryName"
                              id="responsibleLaboratoryName"
                              label="Responsible Laboratory"
                              value={values.responsibleLaboratoryName}
                              type="responsibleLaboratoryName"
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <InfoBox text="Indicate the side of the tape you want to test. If you want to test both sides of a tape, please create two product samples. One per side!">
                              <CustomDropdown
                                mandatory
                                disabled={formFieldsDisabled}
                                labelText="Side of the Product"
                                name="sideOfProduct"
                                id="sideOfProduct"
                                value={values.sideOfProduct}
                                type="sideOfProduct"
                                error={!!(errors.sideOfProduct && touched.sideOfProduct)}
                                onChange={handleChange}
                              >
                                {PRODUCT_SIDES.map((ps) => (
                                  <MenuItem value={ps}>{PRODUCT_SIDE_DICTIONARY[ps] || ""}</MenuItem>
                                ))}
                              </CustomDropdown>
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomRadioGroup
                              disabled={formFieldsDisabled}
                              error={!!(errors.deliveredBy && touched.deliveredBy)}
                              formLabel="Delivered By"
                              mandatory
                              name="deliveredBy"
                              options={["CSC Lab", "Requester"]}
                              onChange={handleChange}
                              value={values.deliveredBy}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              helperText="When the roll was produced"
                              label="Manufacturing Date"
                              value={values.manufacturingDate}
                              onChange={(date) => setFieldValue("manufacturingDate", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3} />
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="descriptionOrNotes"
                              id="descriptionOrNotes"
                              label="Description or Notes"
                              value={values.descriptionOrNotes}
                              type="descriptionOrNotes"
                              helperText={[
                                errors.descriptionOrNotes && touched.descriptionOrNotes
                                  ? errors.descriptionOrNotes
                                  : "",
                              ].join(" ")}
                              error={!!(errors.descriptionOrNotes && touched.descriptionOrNotes)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box display="flex" flexDirection="row">
                        {showGalleryAddButton && (
                          <Box marginRight={2}>
                            <GalleryUploadButton hideIfNonEmpty label="Add Attachment" />
                          </Box>
                        )}
                        {showExtraSaveButton && (
                          <GalleryButton
                            icon="ADD"
                            text="Add Attachment"
                            onClick={() =>
                              showConfirmationDialog({
                                headline: "Save Product Sample",
                                text: "You have to save the Product Sample to add attachments.",
                                confirmText: "Save",
                              })
                                .then(() => {
                                  return formRef?.validateForm();
                                })
                                .then((formErrors) => {
                                  if (formErrors && Object.keys(formErrors).length > 0) {
                                    formRef?.submitForm();
                                    throw new HiddenError("form validation failed");
                                  }
                                  return formRef?.values;
                                })
                                .then((formValues) => {
                                  if (formValues) {
                                    return save(formValues).then((response) => {
                                      callback(false);
                                      formRef?.setFieldValue("productSampleID", response.id || null);
                                      updateProductObject(response);
                                    });
                                  }
                                })
                                .catch(handleHttpError)
                            }
                          />
                        )}
                      </Box>
                      {showGallery && (
                        <ProductSampleGallery
                          editable={permissions?.editAttachments || false}
                          testRequestId={testRequestID}
                          productSampleId={productObject?.id}
                          productSampleName={sampleName}
                          productSampleAttachments={prototypeObject?.attachments as ProductAttachment[]}
                          onChange={(attachments) => {
                            const existingAttachments = prototypeObject?.attachments;
                            if (existingAttachments) {
                              existingAttachments.splice(0, existingAttachments.length, ...attachments);
                            }
                          }}
                        />
                      )}
                      <Grid container direction="row" justify="flex-end">
                        <CustomButton
                          disabled={isSubmitting}
                          primary
                          text={permissions?.editDefinition ? "Save and Close" : "Close"}
                          type="submit"
                        />
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </CustomDialog>
          );
        }}
      </ProductContext.Consumer>
      {/* Confidential confirmation dialog */}
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          objectName="prototype sample"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
          onProceed={() => {
            if (pendingValues && testRequestID) {
              testRequestApi
                .updateConfidentiality(testRequestID, "HIGHLY_CONFIDENTIAL")
                .then(() => {
                  showSnackbar("Test request set to highly confidential", "success");
                  return save(pendingValues);
                })
                .then(() => {
                  formRef?.resetForm();
                  callback(true);
                  onClose();
                  showSnackbar("Prototype sample updated", "success");
                })
                .catch(handleHttpError);
            }
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
        />
      )}
    </>
  );
};

export default ProductSampleDetailsPrototypeDialog;
